import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from "react";

export type GameFieldErrorTarget = {
    highlightKey: string;
    scrollId: string;
};

export type GameStatusPopUpState = {
    title: string;
    message: string;
    type: "info" | "success" | "error";
    fieldError?: GameFieldErrorTarget;
};

export const GAME_FIELD_ERROR_BORDER_CLASS = "border-red-500";
export const GAME_FIELD_NORMAL_BORDER_CLASS = "border-[#a8a8a8]";

export function getGameFieldBorderClass(
    isError: boolean,
    normalClass: string = GAME_FIELD_NORMAL_BORDER_CLASS
) {
    return isError ? GAME_FIELD_ERROR_BORDER_CLASS : normalClass;
}

export function scrollToGameField(scrollId: string) {
    const element = document.getElementById(scrollId);
    if (!element) {
        return;
    }

    element.scrollIntoView({ behavior: "smooth", block: "center" });

    const focusable = element.querySelector("input, textarea, select") as HTMLElement | null;
    focusable?.focus({ preventScroll: true });
}

export function matchingItemFieldTarget(
    itemId: number,
    field: "keyword" | "matchingWord" | "hint"
): GameFieldErrorTarget {
    return {
        highlightKey: `item:${itemId}:${field}`,
        scrollId: `game-field-item-${itemId}-${field}`,
    };
}

export function whackAMoleItemFieldTarget(
    itemId: number,
    field: "correct" | "wrong"
): GameFieldErrorTarget {
    return {
        highlightKey: `item:${itemId}:${field}`,
        scrollId: `game-field-item-${itemId}-${field}`,
    };
}

export function anagramItemFieldTarget(
    itemId: number,
    field: "word" | "swap"
): GameFieldErrorTarget {
    return {
        highlightKey: `item:${itemId}:${field}`,
        scrollId: `game-field-item-${itemId}-${field}`,
    };
}

export function completeSentenceFieldTarget(
    itemId: number,
    field: "sentence" | "blank-correct" | "blank-wrong",
    blankIndex?: number
): GameFieldErrorTarget {
    const suffix = blankIndex !== undefined ? `-${blankIndex}` : "";

    return {
        highlightKey: `cts:${itemId}:${field}${suffix}`,
        scrollId: `game-field-cts-${itemId}-${field}${suffix}`,
    };
}

export function quizQuestionFieldTarget(
    questionId: number,
    field: "question" | "hint" | "answers" = "question"
): GameFieldErrorTarget {
    return {
        highlightKey: `question:${questionId}:${field}`,
        scrollId: `game-field-question-${questionId}-${field}`,
    };
}

type QuizQuestionCorrectChoiceLike = {
    id: number;
    answers: Array<{ isCorrect: boolean }>;
};

export function findQuizQuestionMissingCorrectChoice<T extends QuizQuestionCorrectChoiceLike>(
    questions: T[]
): { questionIndex: number; question: T } | null {
    for (let questionIndex = 0; questionIndex < questions.length; questionIndex += 1) {
        const question = questions[questionIndex];
        const hasCorrectChoice =
            Array.isArray(question.answers) &&
            question.answers.some((answer) => Boolean(answer.isCorrect));

        if (!hasCorrectChoice) {
            return { questionIndex, question };
        }
    }

    return null;
}

export function quizChoiceFieldTarget(questionId: number, choiceId: number): GameFieldErrorTarget {
    return {
        highlightKey: `choice:${questionId}:${choiceId}`,
        scrollId: `game-field-choice-${questionId}-${choiceId}`,
    };
}

export function createGameFieldErrorPopUp(
    title: string,
    message: string,
    fieldError: GameFieldErrorTarget
): GameStatusPopUpState {
    return {
        title,
        message,
        type: "error",
        fieldError,
    };
}

export function useGameFieldErrorHighlight() {
    const [fieldErrorHighlightKey, setFieldErrorHighlightKey] = useState<string | null>(null);
    const pendingScrollIdRef = useRef<string | null>(null);

    const isFieldError = useCallback(
        (highlightKey: string) => fieldErrorHighlightKey === highlightKey,
        [fieldErrorHighlightKey]
    );

    const getFieldBorderClass = useCallback(
        (highlightKey: string, normalClass: string = GAME_FIELD_NORMAL_BORDER_CLASS) =>
            getGameFieldBorderClass(isFieldError(highlightKey), normalClass),
        [isFieldError]
    );

    const clearFieldErrorHighlight = useCallback(() => {
        setFieldErrorHighlightKey(null);
        pendingScrollIdRef.current = null;
    }, []);

    const openStatusPopUp = useCallback(
        (setStatusPopUp: Dispatch<SetStateAction<GameStatusPopUpState | null>>, popup: GameStatusPopUpState) => {
            if (popup.type === "error" && popup.fieldError) {
                setFieldErrorHighlightKey(popup.fieldError.highlightKey);
                pendingScrollIdRef.current = popup.fieldError.scrollId;
            } else {
                setFieldErrorHighlightKey(null);
                pendingScrollIdRef.current = null;
            }

            setStatusPopUp({
                title: popup.title,
                message: popup.message,
                type: popup.type,
            });
        },
        []
    );

    const closeStatusPopUp = useCallback(
        (
            setStatusPopUp: Dispatch<SetStateAction<GameStatusPopUpState | null>>,
            onClosed?: () => void
        ) => {
            setStatusPopUp(null);

            const scrollId = pendingScrollIdRef.current;
            pendingScrollIdRef.current = null;

            if (scrollId) {
                requestAnimationFrame(() => {
                    scrollToGameField(scrollId);
                });
            }

            onClosed?.();
        },
        []
    );

    return {
        fieldErrorHighlightKey,
        isFieldError,
        getFieldBorderClass,
        clearFieldErrorHighlight,
        openStatusPopUp,
        closeStatusPopUp,
    };
}
